import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import OnboardingBanner from '../components/OnboardingBanner';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { assetNavigationHref } from '../utils/assetOpener';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerWellness = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('nutrition');
  const [athleteId, setAthleteId] = useState(null);
  
  // Dialog states
  const [viewMaterialDialog, setViewMaterialDialog] = useState(false);
  const [viewTaskDialog, setViewTaskDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [completionNotes, setCompletionNotes] = useState('');

  // Daily check-in state (Player only)
  const todayStr = new Date().toISOString().slice(0, 10);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]); // last 7 days
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [checkinForm, setCheckinForm] = useState({
    fatigue: 7,
    sleep_quality: 7,
    stress: 7,
    muscle_soreness: 7,
    mental_readiness: 7,
    notes: ''
  });
  const [submittingCheckin, setSubmittingCheckin] = useState(false);

  useEffect(() => {
    if (!['Player', 'Parent'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [materialsRes, tasksRes, meRes] = await Promise.all([
        axios.get(`${API}/wellness/materials`),
        axios.get(`${API}/wellness/tasks`),
        axios.get(`${API}/auth/me`)
      ]);
      setMaterials(materialsRes.data);
      setTasks(tasksRes.data);
      
      // Get progress if athlete is linked
      if (meRes.data.athlete_id) {
        setAthleteId(meRes.data.athlete_id);
        const progressRes = await axios.get(`${API}/wellness/athlete/${meRes.data.athlete_id}/progress`);
        setProgress(progressRes.data);
        // Fetch 7-day check-in history for Player (for trend mini-chart and today detection)
        if (user?.role === 'Player') {
          try {
            const checkinsRes = await axios.get(`${API}/wellness-checkins`, {
              params: { athlete_id: meRes.data.athlete_id, days: 7 }
            });
            const list = Array.isArray(checkinsRes.data) ? checkinsRes.data : [];
            setRecentCheckins(list);
            const today = list.find(c => c.date === todayStr);
            if (today) setTodayCheckin(today);
          } catch { /* ignore */ }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCheckin = async () => {
    if (!athleteId) return;
    setSubmittingCheckin(true);
    try {
      const res = await axios.post(`${API}/wellness-checkins`, {
        athlete_id: athleteId,
        date: todayStr,
        fatigue: checkinForm.fatigue,
        sleep_quality: checkinForm.sleep_quality,
        stress: checkinForm.stress,
        muscle_soreness: checkinForm.muscle_soreness,
        mental_readiness: checkinForm.mental_readiness,
        notes: checkinForm.notes || null,
        source: 'athlete'
      });
      setTodayCheckin(res.data);
      setRecentCheckins(prev => {
        const others = prev.filter(c => c.date !== res.data.date);
        return [res.data, ...others];
      });
      setJustSubmitted(true);
      toast.success('Check-in saved — your coach has been notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit check-in');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  const viewMaterial = async (material) => {
    try {
      const response = await axios.get(`${API}/wellness/materials/${material.id}`);
      setSelectedItem(response.data);
      setViewMaterialDialog(true);
    } catch (error) {
      toast.error('Failed to load material');
    }
  };

  const viewTask = async (task) => {
    setSelectedItem(task);
    setCompletionNotes(task.completion?.completion_notes || '');
    setViewTaskDialog(true);
  };

  const handleToggleCompletion = async (isCompleting) => {
    if (!selectedItem) return;
    
    try {
      await axios.post(`${API}/wellness/tasks/${selectedItem.id}/complete`, {
        is_completed: isCompleting,
        completion_notes: completionNotes
      });
      toast.success(isCompleting ? 'Task marked as complete!' : 'Task marked as incomplete');
      setViewTaskDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const getMaterialTypeIcon = (type) => {
    const icons = {
      'video': '🎬',
      'pdf': '📄',
      'diet_plan': '🍽️',
      'nutrition_guide': '🥗',
      'worksheet': '📝',
      'exercise': '🏃',
      'progress_tracker': '📊',
      'other': '📋'
    };
    return icons[type] || '📋';
  };

  const filterByCategory = (items, category) => {
    return items.filter(item => item.category === category);
  };

  const getCategoryProgress = (category) => {
    if (!progress) return { materials: 0, tasks: 0 };
    
    const data = category === 'nutrition' ? progress.nutrition : progress.mental_training;
    return {
      materials: data.materials_total > 0 
        ? Math.round((data.materials_viewed / data.materials_total) * 100) 
        : 0,
      tasks: data.tasks_total > 0 
        ? Math.round((data.tasks_completed / data.tasks_total) * 100) 
        : 0
    };
  };

  if (!['Player', 'Parent'].includes(user?.role)) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {user?.role === 'Parent' ? "Your Child's" : 'My'} Wellness
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Nutrition plans, mental training, and assigned tasks</p>
        </div>

        {/* Onboarding banner */}
        {user?.role === 'Player' && (
          <OnboardingBanner
            id="player-wellness-v1"
            description="Submit today's check-in in 30 seconds, track your 7-day trends, and complete wellness tasks assigned by your coach."
          />
        )}
        {user?.role === 'Parent' && (
          <OnboardingBanner
            id="parent-wellness-v1"
            description="Review the nutrition plans, mental training materials, and wellness tasks your coach has assigned to your child."
          />
        )}

        {/* Daily Check-in — Player only */}
        {user?.role === 'Player' && athleteId && (
          <Card className="mb-6 border-emerald-200 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20" data-testid="daily-checkin-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span>🌅</span> Today's Check-in
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Rate how you're feeling today on a scale of 1–10 (1 = worst, 10 = best).
                  </CardDescription>
                </div>
                {todayCheckin && (
                  <Badge className="bg-emerald-600 text-white" data-testid="checkin-submitted-badge">
                    ✓ Submitted today
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {todayCheckin ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="checkin-submitted-view">
                    {[
                      { key: 'fatigue', label: 'Energy' },
                      { key: 'sleep_quality', label: 'Sleep' },
                      { key: 'stress', label: 'Calm' },
                      { key: 'muscle_soreness', label: 'Recovery' },
                      { key: 'mental_readiness', label: 'Focus' },
                    ].map(({ key, label }) => (
                      <div key={key} className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-emerald-200 dark:border-emerald-900/40">
                        <p className="text-[10px] uppercase text-gray-500">{label}</p>
                        <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{todayCheckin[key]}<span className="text-xs text-gray-400">/10</span></p>
                      </div>
                    ))}
                  </div>
                  {justSubmitted && (
                    <div className="mt-3 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5" data-testid="checkin-next-step">
                      <span>✓</span>
                      <span>Saved. Your coach has been notified. Check your 7-day trend below ↓</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3" data-testid="checkin-form">
                  {[
                    { key: 'fatigue', label: 'Energy level', hint: '1 = exhausted, 10 = fully energized' },
                    { key: 'sleep_quality', label: 'Sleep quality', hint: '1 = terrible, 10 = excellent' },
                    { key: 'stress', label: 'Feeling calm', hint: '1 = very stressed, 10 = calm' },
                    { key: 'muscle_soreness', label: 'Recovery', hint: '1 = very sore, 10 = no soreness' },
                    { key: 'mental_readiness', label: 'Mental focus', hint: '1 = not ready, 10 = fully focused' },
                  ].map(({ key, label, hint }) => (
                    <div key={key} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between items-baseline">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
                          <span className="text-xs text-gray-400">{hint}</span>
                        </div>
                        <input
                          type="range"
                          min={1}
                          max={10}
                          value={checkinForm[key]}
                          onChange={e => setCheckinForm(f => ({ ...f, [key]: parseInt(e.target.value) }))}
                          className="w-full mt-1 accent-emerald-600"
                          data-testid={`checkin-${key}`}
                        />
                      </div>
                      <div className="w-10 text-center">
                        <span className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{checkinForm[key]}</span>
                      </div>
                    </div>
                  ))}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes (optional)</label>
                    <Textarea
                      value={checkinForm.notes}
                      onChange={e => setCheckinForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Anything your coach should know?"
                      rows={2}
                      className="mt-1"
                      data-testid="checkin-notes"
                    />
                  </div>
                  <Button
                    onClick={handleSubmitCheckin}
                    disabled={submittingCheckin}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="submit-checkin-btn"
                  >
                    {submittingCheckin ? 'Submitting…' : 'Submit Today\'s Check-in'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 7-day Wellness Trend Mini-Chart — Player only */}
        {user?.role === 'Player' && athleteId && (
          <Card className="mb-6 border-gray-200 dark:border-gray-700" data-testid="wellness-trend-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" /> 7-Day Wellness Trend
                  </CardTitle>
                  <CardDescription className="text-xs mt-0.5">Your overall score (average of all 5 metrics)</CardDescription>
                </div>
                {recentCheckins.length >= 2 && (() => {
                  const sorted = [...recentCheckins].sort((a, b) => a.date.localeCompare(b.date));
                  const first = sorted[0].overall_score;
                  const last = sorted[sorted.length - 1].overall_score;
                  const diff = (last - first).toFixed(1);
                  const up = last > first;
                  const flat = Math.abs(last - first) < 0.3;
                  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
                  const color = flat ? 'text-gray-500' : up ? 'text-emerald-600' : 'text-amber-600';
                  return (
                    <div className={`flex items-center gap-1 text-xs font-medium ${color}`} data-testid="trend-delta">
                      <Icon className="w-3.5 h-3.5" />
                      <span>{flat ? 'Stable' : `${up ? '+' : ''}${diff} pts`}</span>
                    </div>
                  );
                })()}
              </div>
            </CardHeader>
            <CardContent>
              {recentCheckins.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No check-ins yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Submit your daily check-in to start building a trend.</p>
                </div>
              ) : recentCheckins.length === 1 ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">1 check-in on record — great start!</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Submit daily to see patterns emerge over the week.</p>
                </div>
              ) : (
                <div className="flex items-end gap-1.5 h-24" data-testid="trend-bars">
                  {(() => {
                    // Build last 7 days with either the check-in score or null
                    const byDate = {};
                    recentCheckins.forEach(c => { byDate[c.date] = c; });
                    const days = [];
                    for (let i = 6; i >= 0; i--) {
                      const d = new Date();
                      d.setDate(d.getDate() - i);
                      const iso = d.toISOString().slice(0, 10);
                      days.push({ date: iso, c: byDate[iso] || null, label: d.toLocaleDateString('en-GB', { weekday: 'short' }) });
                    }
                    return days.map((d, i) => {
                      const score = d.c?.overall_score;
                      const h = score ? Math.max((score / 10) * 100, 8) : 4;
                      const color = !score ? 'bg-gray-100 dark:bg-gray-700'
                        : score >= 7 ? 'bg-emerald-500'
                        : score >= 5 ? 'bg-amber-500'
                        : 'bg-red-400';
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1" title={d.c ? `${d.date}: ${score}/10` : `${d.date}: no check-in`}>
                          <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 h-3">{score ? score : ''}</span>
                          <div className={`w-full rounded-t ${color} transition-all`} style={{ height: `${h}%` }} />
                          <span className="text-[9px] text-gray-400">{d.label.slice(0, 2)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="nutrition" className="gap-2">
              <span>🥗</span> Nutrition & Diet
            </TabsTrigger>
            <TabsTrigger value="mental_training" className="gap-2">
              <span>🧠</span> Mental Training
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="space-y-4" data-testid="wellness-loading-skeleton">
              <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
                <div className="h-64 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
              </div>
            </div>
          ) : (
            ['nutrition', 'mental_training'].map(category => {
              const categoryProgress = getCategoryProgress(category);
              const categoryMaterials = filterByCategory(materials, category);
              const categoryTasks = filterByCategory(tasks, category);
              
              return (
                <TabsContent key={category} value={category}>
                  {/* Progress Card */}
                  <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Your Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">Materials Viewed</span>
                            <span className="text-sm text-gray-500 dark:text-gray-500">{categoryProgress.materials}%</span>
                          </div>
                          <Progress value={categoryProgress.materials} className="h-2" />
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">Tasks Completed</span>
                            <span className="text-sm text-gray-500 dark:text-gray-500">{categoryProgress.tasks}%</span>
                          </div>
                          <Progress value={categoryProgress.tasks} className="h-2" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tasks Section - Show first for action items */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-700">
                            ✓
                          </span>
                          My Tasks
                        </CardTitle>
                        <CardDescription>
                          {categoryTasks.filter(t => !t.is_completed).length} pending tasks
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {categoryTasks.length === 0 ? (
                          <p className="text-gray-500 dark:text-gray-500 text-center py-8">No tasks assigned</p>
                        ) : (
                          <div className="space-y-3">
                            {categoryTasks.map(task => (
                              <div 
                                key={task.id}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                  task.is_completed 
                                    ? 'bg-emerald-50 border border-emerald-200' 
                                    : 'bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:bg-gray-700'
                                }`}
                                onClick={() => viewTask(task)}
                                data-testid={`task-${task.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    task.is_completed ? 'bg-emerald-500 text-white' : 'border-2 border-gray-300 dark:border-gray-600'
                                  }`}>
                                    {task.is_completed && '✓'}
                                  </div>
                                  <div>
                                    <p className={`font-medium text-sm ${task.is_completed ? 'line-through text-gray-500 dark:text-gray-500' : ''}`}>
                                      {task.title}
                                    </p>
                                    {task.due_date && (
                                      <p className="text-xs text-gray-500 dark:text-gray-500">Due: {task.due_date}</p>
                                    )}
                                  </div>
                                </div>
                                {task.is_completed ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">Done</Badge>
                                ) : (
                                  <Badge variant="outline">Pending</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Materials Section */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                            📚
                          </span>
                          Resources
                        </CardTitle>
                        <CardDescription>
                          {categoryMaterials.length} materials available
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {categoryMaterials.length === 0 ? (
                          <p className="text-gray-500 dark:text-gray-500 text-center py-8">No materials available</p>
                        ) : (
                          <div className="space-y-3">
                            {categoryMaterials.map(material => (
                              <div 
                                key={material.id}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                  material.viewed ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-emerald-300'
                                }`}
                                onClick={() => viewMaterial(material)}
                                data-testid={`material-${material.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-xl">
                                    {getMaterialTypeIcon(material.material_type)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{material.title}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                      {material.material_type.replace('_', ' ')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {material.is_required && (
                                    <Badge className="bg-red-100 text-red-700">Required</Badge>
                                  )}
                                  {material.viewed && (
                                    <Badge variant="outline" className="text-emerald-600">Viewed</Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              );
            })
          )}
        </Tabs>

        {/* View Material Dialog */}
        <Dialog open={viewMaterialDialog} onOpenChange={setViewMaterialDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-2xl">{getMaterialTypeIcon(selectedItem?.material_type)}</span>
                {selectedItem?.title}
              </DialogTitle>
              {selectedItem?.is_required && (
                <Badge className="w-fit bg-red-100 text-red-700">Required</Badge>
              )}
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                {selectedItem.description && (
                  <div>
                    <p className="text-gray-700 dark:text-gray-300">{selectedItem.description}</p>
                  </div>
                )}
                
                {selectedItem.content && (
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-sans">
                      {selectedItem.content}
                    </pre>
                  </div>
                )}
                
                {selectedItem.file_url && (
                  <div className="border rounded-lg p-4">
                    <a 
                      href={assetNavigationHref(selectedItem.file_url, selectedItem.file_name || selectedItem.title)}
                      data-testid="player-material-file-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-pfap-green hover:text-pfap-green/80"
                    >
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center text-2xl">
                        📎
                      </div>
                      <div>
                        <p className="font-medium">Download File</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">{selectedItem.file_name}</p>
                      </div>
                    </a>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Added by {selectedItem.created_by_name} • {new Date(selectedItem.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Task Dialog */}
        <Dialog open={viewTaskDialog} onOpenChange={setViewTaskDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedItem?.title}</DialogTitle>
              {selectedItem?.due_date && (
                <DialogDescription>Due: {selectedItem.due_date}</DialogDescription>
              )}
            </DialogHeader>
            
            {selectedItem && (
              <div className="space-y-4 py-4">
                {selectedItem.description && (
                  <div>
                    <p className="text-gray-700 dark:text-gray-300">{selectedItem.description}</p>
                  </div>
                )}
                
                {selectedItem.instructions && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Instructions:</p>
                    <p className="text-sm text-blue-700 whitespace-pre-wrap">{selectedItem.instructions}</p>
                  </div>
                )}
                
                {selectedItem.material && (
                  <div className="border rounded-lg p-3">
                    <p className="text-sm text-gray-500 dark:text-gray-500 mb-1">Related Material:</p>
                    <p className="font-medium text-emerald-600">{selectedItem.material.title}</p>
                  </div>
                )}
                
                {user?.role === 'Player' && (
                  <>
                    <div>
                      <p className="text-sm font-medium mb-2">Notes (optional)</p>
                      <Textarea 
                        value={completionNotes}
                        onChange={e => setCompletionNotes(e.target.value)}
                        placeholder="Add any notes about completing this task..."
                        rows={3}
                      />
                    </div>
                    
                    {selectedItem.completion?.staff_feedback && (
                      <div className="bg-amber-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-amber-800">Coach Feedback:</p>
                        <p className="text-sm text-amber-700">{selectedItem.completion.staff_feedback}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            {user?.role === 'Player' && (
              <DialogFooter>
                <Button variant="outline" onClick={() => setViewTaskDialog(false)}>
                  Close
                </Button>
                {selectedItem?.is_completed ? (
                  <Button 
                    variant="outline"
                    onClick={() => handleToggleCompletion(false)}
                  >
                    Mark Incomplete
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleToggleCompletion(true)}
                    className="bg-pfap-green hover:bg-pfap-green/90"
                  >
                    Mark as Complete
                  </Button>
                )}
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PlayerWellness;
